import { memo } from 'react';
import { FormBuilderPreviewCheckbox } from '../FormBuilderPreviewCheckbox';
import { FormBuilderPreviewDate } from '../FormBuilderPreviewDate';
import { FormBuilderPreviewDropdown } from '../FormBuilderPreviewDropdown';
import { FormBuilderPreviewFile } from '../FormBuilderPreviewFile';
import { FormBuilderPreviewMultilineInput } from '../FormBuilderPreviewMultilineInput';
import { FormBuilderPreviewMultipleChoice } from '../FormBuilderPreviewMultipleChoice';
import { FormBuilderPreviewSingleChoice } from '../FormBuilderPreviewSingleChoice';
import { FormBuilderPreviewSingleLineInput } from '../FormBuilderPreviewSingleLineInput';
import { FormBuilderPreviewText } from '../FormBuilderPreviewText';
import { FormBuilderPreviewTextEditor } from '../FormBuilderPreviewTextEditor';
import {
  EFormBuilderFieldType,
  TFormBuilderField,
  TFormBuilderPreviewModeSize,
} from '@/types';

type TProps = {
  field: TFormBuilderField;
  modeSize: TFormBuilderPreviewModeSize;
};

export const FormBuilderPreviewFieldFactory =
  /**
   * Factory component that renders the appropriate preview field based on field type
   * @param {TProps} props - Component props
   * @param {TFormBuilderField} props.field - Form field data to preview
   * @param {TFormBuilderPreviewModeSize} props.modeSize - Preview mode size ('mobile' or 'desktop') to control the display format
   * @returns {JSX.Element|null} Corresponding form builder preview field component or null
   */
  memo(({ field, modeSize }: TProps) => {
    switch (field.type) {
      case EFormBuilderFieldType.TEXT:
        return <FormBuilderPreviewText field={field} />;

      case EFormBuilderFieldType.CHECKBOX:
        return <FormBuilderPreviewCheckbox field={field} />;

      case EFormBuilderFieldType.SINGLE_LINE_INPUT:
        return <FormBuilderPreviewSingleLineInput field={field} />;

      case EFormBuilderFieldType.MULTILINE_INPUT:
        return <FormBuilderPreviewMultilineInput field={field} />;

      case EFormBuilderFieldType.TEXT_EDITOR:
        return <FormBuilderPreviewTextEditor field={field} />;

      case EFormBuilderFieldType.SINGLE_CHOICE:
        return <FormBuilderPreviewSingleChoice field={field} />;

      case EFormBuilderFieldType.MULTIPLE_CHOICE:
        return <FormBuilderPreviewMultipleChoice field={field} />;

      case EFormBuilderFieldType.DROPDOWN:
        return <FormBuilderPreviewDropdown field={field} />;

      case EFormBuilderFieldType.DATE:
        return <FormBuilderPreviewDate field={field} modeSize={modeSize} />;

      case EFormBuilderFieldType.FILE:
        return <FormBuilderPreviewFile field={field} />;

      default:
        return null;
    }
  });

FormBuilderPreviewFieldFactory.displayName = 'FormBuilderPreviewFieldFactory';
